import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Send, Plus, Search, Trash2, Mic, Paperclip, Sparkles,
  MessageSquare, MoreHorizontal, X, Loader2, User, ChevronDown
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { getAuthHeaders, getAuthToken } from '../utils/auth';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ── Suggestion Chips ──────────────────────────────
const SUGGESTIONS = [
  { label: 'Build business plan', icon: Sparkles },
  { label: 'Analyze my idea', icon: Sparkles },
  { label: 'Draft a document', icon: Sparkles },
  { label: 'Think through a decision', icon: Sparkles },
];

// ── Message Bubble ────────────────────────────────
const MessageBubble = ({ message, isStreaming }) => {
  const isUser = message.role === 'user';

  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} group`}>
      <div className={`flex gap-3 max-w-2xl ${isUser ? 'flex-row-reverse' : ''}`}>
        {/* Avatar */}
        <div className={`w-7 h-7 rounded-lg flex-shrink-0 flex items-center justify-center mt-1 ${
          isUser ? 'bg-zinc-700' : 'bg-amber-500/15 border border-amber-500/25'
        }`}>
          {isUser ? (
            <User className="w-3.5 h-3.5 text-zinc-300" />
          ) : (
            <span className="text-[10px] font-bold font-mono text-amber-500">L</span>
          )}
        </div>

        {/* Bubble */}
        <div className={`rounded-2xl px-4 py-3 ${
          isUser
            ? 'bg-zinc-800 border border-zinc-700/50'
            : 'bg-zinc-900/60 border border-zinc-800/50'
        }`}>
          <p className={`text-[14px] leading-relaxed whitespace-pre-wrap ${
            isUser ? 'text-zinc-100' : 'text-zinc-300'
          }`}>
            {message.content}
            {isStreaming && (
              <span className="inline-block w-[2px] h-[16px] bg-amber-500 ml-0.5 animate-pulse align-middle" />
            )}
          </p>
        </div>
      </div>
    </div>
  );
};

// ── Thinking Indicator ────────────────────────────
const ThinkingIndicator = () => (
  <div className="flex justify-start">
    <div className="flex gap-3 max-w-2xl">
      <div className="w-7 h-7 rounded-lg bg-amber-500/15 border border-amber-500/25 flex items-center justify-center mt-1 flex-shrink-0">
        <span className="text-[10px] font-bold font-mono text-amber-500">L</span>
      </div>
      <div className="rounded-2xl bg-zinc-900/60 border border-zinc-800/50 px-4 py-3">
        <div className="flex items-center gap-2.5">
          <div className="flex gap-1">
            <div className="w-1.5 h-1.5 bg-amber-500/60 rounded-full animate-bounce" style={{ animationDelay: '0ms', animationDuration: '0.8s' }} />
            <div className="w-1.5 h-1.5 bg-amber-500/60 rounded-full animate-bounce" style={{ animationDelay: '160ms', animationDuration: '0.8s' }} />
            <div className="w-1.5 h-1.5 bg-amber-500/60 rounded-full animate-bounce" style={{ animationDelay: '320ms', animationDuration: '0.8s' }} />
          </div>
          <span className="text-xs text-zinc-600 font-mono">Thinking</span>
        </div>
      </div>
    </div>
  </div>
);

// ── Sidebar Conversation Item ─────────────────────
const ConversationItem = ({ conv, isActive, onClick, onDelete }) => (
  <button
    onClick={onClick}
    className={`w-full text-left px-3 py-2.5 rounded-lg transition-all duration-150 group flex items-center gap-2.5 ${
      isActive
        ? 'bg-amber-500/10 text-white'
        : 'text-zinc-500 hover:text-zinc-300 hover:bg-white/[0.03]'
    }`}
  >
    <MessageSquare className="w-3.5 h-3.5 flex-shrink-0 opacity-50" />
    <span className="text-[13px] truncate flex-1">{conv.title || 'New conversation'}</span>
    <button
      onClick={(e) => { e.stopPropagation(); onDelete(conv.conversation_id); }}
      className="opacity-0 group-hover:opacity-100 p-0.5 hover:text-red-400 transition-all"
    >
      <Trash2 className="w-3 h-3" />
    </button>
  </button>
);

// ══════════════════════════════════════════════════
// ── MAIN COMPONENT ────────────────────────────────
// ══════════════════════════════════════════════════
const PersistentIntelligence = () => {
  const navigate = useNavigate();

  // State
  const [conversations, setConversations] = useState([]);
  const [activeConvId, setActiveConvId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isThinking, setIsThinking] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showScrollBtn, setShowScrollBtn] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);

  // Refs
  const messagesEndRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const inputRef = useRef(null);
  const abortRef = useRef(null);

  // ── Load conversations ──────────────────────────
  const loadConversations = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/conversations`, { headers: getAuthHeaders() });
      setConversations(res.data.conversations || []);
    } catch (e) {
      console.error('Failed to load conversations:', e);
    }
  }, []);

  // ── Load messages for a conversation ────────────
  const loadMessages = useCallback(async (convId) => {
    try {
      const res = await axios.get(`${API}/conversations/${convId}/messages`, { headers: getAuthHeaders() });
      setMessages((res.data.messages || []).map(m => ({ role: m.role, content: m.content })));
    } catch (e) {
      console.error('Failed to load messages:', e);
    }
  }, []);

  useEffect(() => { loadConversations(); }, [loadConversations]);

  useEffect(() => {
    if (activeConvId) loadMessages(activeConvId);
    else setMessages([]);
  }, [activeConvId, loadMessages]);

  // ── Scroll helpers ──────────────────────────────
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(scrollToBottom, [messages, isThinking, scrollToBottom]);

  const handleScroll = () => {
    if (!scrollContainerRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
    setShowScrollBtn(scrollHeight - scrollTop - clientHeight > 120);
  };

  // ── New conversation ────────────────────────────
  const newConversation = () => {
    setActiveConvId(null);
    setMessages([]);
    setInput('');
    inputRef.current?.focus();
  };

  // ── Delete conversation ─────────────────────────
  const deleteConversation = async (convId) => {
    try {
      await axios.delete(`${API}/conversations/${convId}`, { headers: getAuthHeaders() });
      setConversations(prev => prev.filter(c => c.conversation_id !== convId));
      if (activeConvId === convId) newConversation();
      toast.success('Conversation deleted');
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  // ── Send message with typewriter effect ──────
  const sendMessage = async (text) => {
    const msg = (text || input).trim();
    if (!msg || isThinking || isStreaming) return;

    const convId = activeConvId || `conv-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    if (!activeConvId) setActiveConvId(convId);

    // Add user message
    setMessages(prev => [...prev, { role: 'user', content: msg }]);
    setInput('');
    setIsThinking(true);

    try {
      // Use regular chat endpoint
      const response = await fetch(`${API}/chat/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getAuthToken()}`
        },
        body: JSON.stringify({ message: msg, conversation_id: convId })
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let fullText = '';
      let hasStartedContent = false;
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // Keep incomplete line in buffer

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const data = JSON.parse(line.slice(6));
            if (data.type === 'content') {
              if (!hasStartedContent) {
                setIsThinking(false);
                setIsStreaming(true);
                setMessages(prev => [...prev, { role: 'assistant', content: '' }]);
                hasStartedContent = true;
              }
              fullText += data.content;
              setMessages(prev => {
                const updated = [...prev];
                updated[updated.length - 1] = { role: 'assistant', content: fullText };
                return updated;
              });
            } else if (data.type === 'done') {
              setIsStreaming(false);
              loadConversations();
            } else if (data.type === 'error') {
              throw new Error(data.content);
            }
          } catch (e) {
            if (e.message && !e.message.includes('JSON')) throw e;
          }
        }
      }

      // If we never got content (proxy buffered everything), parse the full response
      if (!hasStartedContent && buffer) {
        const allLines = buffer.split('\n').filter(l => l.startsWith('data: '));
        setIsThinking(false);
        for (const line of allLines) {
          try {
            const data = JSON.parse(line.slice(6));
            if (data.type === 'content') {
              fullText += data.content;
            }
          } catch (e) { /* skip */ }
        }
        if (fullText) {
          // Typewriter effect for buffered response
          setIsStreaming(true);
          setMessages(prev => [...prev, { role: 'assistant', content: '' }]);
          const words = fullText.split(' ');
          let displayed = '';
          for (let i = 0; i < words.length; i++) {
            displayed += words[i] + ' ';
            const current = displayed;
            setMessages(prev => {
              const updated = [...prev];
              updated[updated.length - 1] = { role: 'assistant', content: current };
              return updated;
            });
            await new Promise(r => setTimeout(r, 20));
          }
          setIsStreaming(false);
          loadConversations();
        }
      }

      // Fallback: if still thinking after stream ends, try regular endpoint
      if (!hasStartedContent && !fullText) {
        const fallback = await axios.post(`${API}/chat`, 
          { message: msg, conversation_id: convId },
          { headers: getAuthHeaders(), timeout: 90000 }
        );
        setIsThinking(false);
        const responseText = fallback.data.response;
        // Typewriter
        setIsStreaming(true);
        setMessages(prev => [...prev, { role: 'assistant', content: '' }]);
        const words = responseText.split(' ');
        let displayed = '';
        for (let i = 0; i < words.length; i++) {
          displayed += words[i] + ' ';
          const current = displayed;
          setMessages(prev => {
            const updated = [...prev];
            updated[updated.length - 1] = { role: 'assistant', content: current };
            return updated;
          });
          await new Promise(r => setTimeout(r, 20));
        }
        setIsStreaming(false);
        loadConversations();
      }

    } catch (err) {
      console.error('Chat error:', err);
      setIsThinking(false);
      setIsStreaming(false);
      
      // Fallback to regular endpoint
      try {
        const fallback = await axios.post(`${API}/chat`, 
          { message: msg, conversation_id: convId },
          { headers: getAuthHeaders(), timeout: 90000 }
        );
        const responseText = fallback.data.response;
        setMessages(prev => [...prev, { role: 'assistant', content: responseText }]);
        loadConversations();
      } catch (e2) {
        toast.error('Failed to get response');
        setMessages(prev => [...prev, { role: 'assistant', content: 'Connection issue — try again.' }]);
      }
    }

    inputRef.current?.focus();
  };

  // ── Keyboard handler ────────────────────────────
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // ── Filter conversations ────────────────────────
  const filtered = conversations.filter(c =>
    !searchQuery || (c.title || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  // ══════════════════════════════════════════════════
  // ── RENDER ──────────────────────────────────────
  // ══════════════════════════════════════════════════
  return (
    <div className="h-screen bg-[#0a0a0a] flex overflow-hidden">

      {/* ── LEFT SIDEBAR ──────────────────────────── */}
      <div className={`${sidebarOpen ? 'w-72' : 'w-0'} flex-shrink-0 bg-[#0f0f0f] border-r border-white/[0.06] flex flex-col transition-all duration-200 overflow-hidden`}>
        
        {/* New Chat Button */}
        <div className="p-3">
          <button
            onClick={newConversation}
            className="w-full flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl border border-white/[0.08] hover:border-amber-500/30 bg-white/[0.02] hover:bg-amber-500/[0.05] text-zinc-400 hover:text-amber-400 transition-all text-[13px]"
          >
            <Plus className="w-4 h-4" />
            New conversation
          </button>
        </div>

        {/* Search */}
        <div className="px-3 mb-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-600" />
            <input
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Search conversations..."
              className="w-full bg-white/[0.03] border border-white/[0.06] rounded-lg pl-9 pr-3 py-2 text-xs text-zinc-300 placeholder:text-zinc-700 focus:outline-none focus:border-zinc-600 transition-colors"
            />
          </div>
        </div>

        {/* Conversation List */}
        <div className="flex-1 overflow-y-auto px-2 pb-4 space-y-0.5 scrollbar-hide">
          {filtered.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-xs text-zinc-700">No conversations yet</p>
            </div>
          ) : (
            filtered.map(conv => (
              <ConversationItem
                key={conv.conversation_id}
                conv={conv}
                isActive={activeConvId === conv.conversation_id}
                onClick={() => setActiveConvId(conv.conversation_id)}
                onDelete={deleteConversation}
              />
            ))
          )}
        </div>

        {/* Sidebar Footer */}
        <div className="p-3 border-t border-white/[0.06]">
          <button
            onClick={() => navigate('/dashboard')}
            className="text-[11px] text-zinc-600 hover:text-zinc-400 transition-colors font-mono"
          >
            ← Dashboard
          </button>
        </div>
      </div>

      {/* ── MAIN CHAT AREA ────────────────────────── */}
      <div className="flex-1 flex flex-col min-w-0">

        {/* Top Bar */}
        <div className="h-12 border-b border-white/[0.04] flex items-center px-4 gap-3 flex-shrink-0">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="text-zinc-600 hover:text-zinc-400 transition-colors lg:hidden"
          >
            <MessageSquare className="w-4 h-4" />
          </button>
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="text-zinc-600 hover:text-zinc-400 transition-colors hidden lg:block"
          >
            <MessageSquare className="w-4 h-4" />
          </button>
          <div className="flex items-center gap-2">
            <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
            <span className="text-[13px] font-medium text-zinc-400">LEE</span>
          </div>
        </div>

        {/* Messages Container */}
        <div
          ref={scrollContainerRef}
          onScroll={handleScroll}
          className="flex-1 overflow-y-auto scrollbar-hide"
        >
          <div className="max-w-3xl mx-auto px-6 py-8">
            
            {/* Empty State */}
            {messages.length === 0 && !isThinking && (
              <div className="flex flex-col items-center justify-center py-24">
                <div className="w-16 h-16 rounded-2xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center mb-8">
                  <span className="text-2xl font-bold font-mono text-amber-500">L</span>
                </div>
                <h2 className="text-xl font-semibold text-white mb-2">What are we working on?</h2>
                <p className="text-zinc-600 text-sm max-w-md text-center mb-10">
                  Talk to LEE about anything — inventions, strategy, decisions, or just thinking out loud.
                </p>

                {/* Quick Actions */}
                <div className="grid grid-cols-2 gap-3 max-w-md w-full mb-6">
                  {[
                    { label: 'Generate', desc: 'Create content, plans, specs', action: 'Generate a detailed business plan for my invention' },
                    { label: 'Analyze', desc: 'Break down ideas, data, markets', action: 'Analyze the market opportunity for a smart savings jar' },
                    { label: 'Build', desc: 'Design, architect, prototype', action: 'Help me build a product roadmap for the next 90 days' },
                    { label: 'Think', desc: 'Reason through decisions', action: 'Help me think through whether to bootstrap or raise funding' },
                  ].map((qa, i) => (
                    <button
                      key={i}
                      onClick={() => sendMessage(qa.action)}
                      className="text-left p-4 rounded-xl bg-white/[0.02] border border-white/[0.06] hover:border-amber-500/20 hover:bg-white/[0.04] transition-all group"
                    >
                      <p className="text-sm font-medium text-zinc-300 group-hover:text-white transition-colors">{qa.label}</p>
                      <p className="text-[11px] text-zinc-600 mt-0.5">{qa.desc}</p>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Message List */}
            <div className="space-y-5">
              {messages.map((msg, idx) => (
                <MessageBubble
                  key={idx}
                  message={msg}
                  isStreaming={isStreaming && idx === messages.length - 1 && msg.role === 'assistant'}
                />
              ))}

              {isThinking && <ThinkingIndicator />}
            </div>

            <div ref={messagesEndRef} />
          </div>
        </div>

        {/* Scroll to Bottom */}
        {showScrollBtn && (
          <div className="flex justify-center -mt-10 mb-2 relative z-10">
            <button
              onClick={scrollToBottom}
              className="p-2 rounded-full bg-zinc-800/90 border border-zinc-700 text-zinc-400 hover:text-white transition-colors shadow-xl backdrop-blur-sm"
            >
              <ChevronDown className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* ── INPUT AREA ──────────────────────────── */}
        <div className="px-4 pb-4 pt-2">
          <div className="max-w-3xl mx-auto">

            {/* Suggestion Chips (only when empty) */}
            {messages.length === 0 && !isThinking && (
              <div className="flex gap-2 mb-3 overflow-x-auto pb-1 scrollbar-hide">
                {SUGGESTIONS.map((s, i) => (
                  <button
                    key={i}
                    onClick={() => sendMessage(s.label)}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-white/[0.08] bg-white/[0.02] hover:border-amber-500/30 hover:bg-amber-500/[0.05] text-zinc-500 hover:text-amber-400 text-xs whitespace-nowrap transition-all"
                  >
                    <s.icon className="w-3 h-3" />
                    {s.label}
                  </button>
                ))}
              </div>
            )}

            {/* Input Bar */}
            <div className="relative flex items-end gap-2 p-2 bg-[#141414] border border-white/[0.08] rounded-2xl focus-within:border-zinc-600 transition-colors shadow-xl shadow-black/20">
              {/* Attachment */}
              <button className="flex-shrink-0 p-2 text-zinc-600 hover:text-zinc-400 transition-colors rounded-lg hover:bg-white/[0.04]">
                <Paperclip className="w-4 h-4" />
              </button>

              {/* Text Input */}
              <textarea
                ref={inputRef}
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Message LEE..."
                rows={1}
                disabled={isThinking || isStreaming}
                className="flex-1 bg-transparent border-0 outline-none text-[14px] text-white placeholder:text-zinc-600 resize-none max-h-32 py-2 px-1 disabled:opacity-50 leading-relaxed"
                style={{ minHeight: '40px' }}
              />

              {/* Voice */}
              <button className="flex-shrink-0 p-2 text-zinc-600 hover:text-zinc-400 transition-colors rounded-lg hover:bg-white/[0.04]">
                <Mic className="w-4 h-4" />
              </button>

              {/* Send */}
              <button
                onClick={() => sendMessage()}
                disabled={!input.trim() || isThinking || isStreaming}
                className="flex-shrink-0 p-2 rounded-xl bg-amber-500 hover:bg-amber-400 text-black disabled:opacity-20 disabled:cursor-not-allowed transition-all"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>

            <p className="text-[10px] text-zinc-700 mt-2 text-center font-mono">
              LEE · Persistent Intelligence
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PersistentIntelligence;
